import FesteaPay from '@assets/images/FesteaPay';
import { Button } from '@atoms';
import { Cart, CartSelection, Coupon, Order, PickupBar, ProductNode, ProductPack, Promotion, SelectionTree } from '@datamodel';
import { useLanguage } from '@languageProvider';
import { useModal, useSnackbar } from '@lib';
import { AddItemModal, AddPackModal, UpsellingModal } from '@modals';
import { CartPackRow, CartRow, FungibleCard, ProductPackCard } from '@molecules';
import { PromotionWidget, PromotionsDrawer } from '@organisms';
import service, { Orders, Trackings } from '@services';
import { Colors, Commons, Icon, Spacing, Typography } from '@styles';
import { useTheme } from '@themeProvider';
import Animation from '@utils/animations';
import { objectFilter, objectMap, parsePrice } from '@utils/helpers';
import { getBeaconIds, setLastPopup, shouldShowPopup } from '@utils/storage';
import { debounce } from '@utils/underscore';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, BackHandler, Image, Pressable, SafeAreaView, ScrollView, StyleSheet, Text, View } from 'react-native';
import { dHeight, dWidth } from '../styles/commons';
import { setCartRecovery } from '../utils/storage';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);
const styles = Commons.styles;

const screenTracker = new Trackings.Tracker('MenuScreen');
const upsellingTracker = new Trackings.Tracker('UpsellingModal');

const TopBar = ({ style, isDark, openDrawer, totalItems, primaryColor, logoURL, ...props }) => {
    const tracker = useRef((Trackings.Tracker.from(screenTracker)).setComponent('TopBar')).current;

    const { useColor } = useTheme();
    const [secondary, background] = useColor([['secondary', { isText: false }], 'background']);
    const barColor = Colors.HEXFilter(primaryColor) || background;
    console.log('barColor', barColor);
    const [onPrimary, onBar] = useColor([
        ['title', { on: secondary }],
        ['title', { on: barColor }]
    ])

    return <View style={[styles.topBar, style, { backgroundColor: barColor }]} {...props}>
        <Pressable onPress={() => {
            tracker.tap('LeftPressable', { action: 'openDrawer.left' });
            openDrawer.left();
        }} style={{ width: 48, height: 48, alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="discount" color={onBar} size={24} style={styles.noSelect} />
        </Pressable>
        {/* <View style={{ width: 48 }}></View> */}
        <Image
            resizeMode='contain'
            style={{ height: 28, flex: 1 }}
            source={{ uri: logoURL }}
        />
        <Pressable onPress={() => {
            tracker.tap('RightPressable', { action: 'openDrawer.right' });
            openDrawer.right();
        }} style={{ width: 48, height: 48, alignItems: 'center', justifyContent: 'center' }}>
            {totalItems > 0 &&
                <View style={{
                    position: 'absolute',
                    right: 2,
                    top: 2,
                    backgroundColor: secondary,
                    height: 20,
                    width: 20,
                    borderRadius: 8,
                    borderWidth: 2,
                    borderColor: barColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    <Text numberOfLines={1} ellipsizeMode="clip" style={[Typography.STRONG, { color: onPrimary, fontSize: 12, textAlign: 'center', marginTop: -3 }]}>{totalItems}</Text>
                </View>
            }
            <Icon name="cart" color={onBar} size={24} style={styles.noSelect} />
        </Pressable>
    </View>
}

const Navbar = forwardRef(({ navbar, scrollToArea, ...props }, ref) => {
    const tracker = useRef(Trackings.Tracker.from(screenTracker).setComponent('Navbar')).current;
    const tagsRef = useRef({});
    const scrollRef = useRef();
    const { useElevation, useColor } = useTheme();
    const elevation = useElevation(2);
    const title = useColor('title');

    useImperativeHandle(ref, () => ({
        scrollNavBarToKey: (key) => {
            scrollRef.current.scrollTo({
                y: 0,
                x: tagsRef.current[key] - Spacing.MARGINS
            })
        }
    }))

    return <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        {...props}
        contentContainerStyle={[{ paddingTop: Spacing.XS, paddingBottom: Spacing.XS, paddingHorizontal: Spacing.MARGINS, gap: Spacing.XS }]}
    >
        {Object.keys(navbar).map(k => (
            <Pressable onLayout={({ nativeEvent }) => {
                tagsRef.current[k] = nativeEvent.layout.x;
            }} key={k} style={[styles.navBarButton, elevation]} onPress={() => {
                tracker.tap('Tag', { title: navbar[k].title });
                scrollToArea(k)
            }}>
                <Text style={[Typography.SUBTITLE, { color: title }]}>{navbar[k].title}</Text>
            </Pressable>
        ))}
    </ScrollView>
})

const INITIAL_SERVER_CART = {
    selections: [],
    promotion_codes: [],
}

// const AddDiscountBanner = ({ style, ...props }) => {
//     const { useColor } = useTheme();
//     const [ secondary, title, body ] = useColor([
//         'secondary',
//         'title',
//         'body'
//     ]);

//     return <Pressable style={[Mixins.HORIZONTAL, { backgroundColor: Colors.setOpacity(secondary, .25)}, style]} {...props}>
//         <Icon name="discount" color={secondary} size={48} />
//         <View>
//         <Text style={[Typography.TITLE, { color: title, }]}>Have a discount?</Text>
//         <Text style={[Typography.BODY, { color: body, }]}>Press here and add it!</Text>
//         </View>
//     </Pressable>
// }

const AspectRatioImage = (props) => {
    const [size, setSize] = useState({ width: 0, height: 1 });

    useEffect(() => {
        Image.getSize(props.source.uri, (width, height) => {
            setSize({ width, height });
        })
    }, [props.source.uri]);

    const style = {
        ...props.style,
        aspectRatio: size.width / size.height,
    };

    const imageProps = {
        ...props,
        style,
        resizeMode: 'contain',
    };
    return <Image {...imageProps} />
}

const WarningBanner = ({ text, style }) => {
    const { useColor } = useTheme();
    const [warning, warningText] = useColor([
        ['warning', { isText: false }],
        'warning',
    ]);

    return <View style={[{ backgroundColor: Colors.setOpacity(warning, .25), flexDirection: 'row', padding: Spacing.M, gap: Spacing.XS, borderRadius: 24, alignItems: 'center' }, style]}>
        <Icon name="warning" color={warning} size={32} />
        <View style={{ flex: 1 }}>
            <Text style={[Typography.BODY, { color: warningText }]}>{text}</Text>
        </View>
    </View>
}

export const MenuScreen = ({
    refreshMenu,
    menu = ProductNode.model,
    promotions,
    addPromotions,
    pickupBars,
    packs,
    place,
    placeId,
    eventId,
    slug,
    isAuth,
    profile,
    minimumOrderAmount,
    primaryColor,
    logoURL,
    address,
    business,
    popupSponsorPhoto,
    popupSponsorUrl,
    popupSponsorCta,
    initialCart,
    ...props }) => {
    ;

    screenTracker.setParams({
        placeId
    })

    const { useColor, useElevation, isDark } = useTheme();
    const { pushActionSheet, pushAlert, pushModal, popModal } = useModal();
    const { pushSnack } = useSnackbar();
    const { translate } = useLanguage();

    const elev2 = useElevation(2);
    const elev12 = useElevation(12);
    const [backgroundColor, body, line, inputBackground, label, title] = useColor([
        'background', 'body', 'line', 'inputBackground', 'label', 'title'
    ]);
    const isRendered = useRef();
    const menuScollRef = useRef();
    const areasLayoutRef = useRef({});
    const totalPacks = useRef(0);
    const rightDrawerAnim = useRef(new Animated.Value(0)).current;
    const leftDrawerAnim = useRef(new Animated.Value(0)).current;
    const buttonAnimation = useRef(new Animated.Value(0)).current;
    const [navbar, setNavbar] = useState({});
    const [delivery, setDelivery] = useState();
    const [finishUpselling, setFinishUpselling] = useState(false);
    const navbarRef = useRef();
    const spinnerModalIndex = useRef();
    const promotionDrawerRef = useRef();
    const upselling = useRef();
    const acceptedSuggestions = useRef([]);

    // Pickupbar modes
    const pbArray = useMemo(() => PickupBar.collectionToArray(pickupBars), [pickupBars]);
    const deliveryId = useMemo(() => PickupBar.getAnyDelivery(pbArray), [pbArray]);
    const pickupId = useMemo(() => PickupBar.getAnyPrepared(pbArray), [pbArray]);

    const [serverCart, setServerCart] = useState(initialCart || INITIAL_SERVER_CART);

    const loadedAtTimeStamp = useMemo(() => {
        return (new Date()).toISOString();
    }, []);

    const updateUpselling = async (serverCart) => {
        if (!serverCart.selections.length || serverCart.selections.length > 10) return;

        try {
            const { suggestion } = await Orders.fetchSuggestion({
                ...serverCart,
                event: eventId,
                place: placeId,
            });

            upselling.current = {
                ...suggestion,
                seen: upselling.current?.seen || 0
            };

        } catch (e) {
            if (e.statusCode == 422) {
                if (e.mainError == 'out_of_stock') {
                    pushSnack({
                        type: 'warning',
                        icon: 'warning',
                        text: translate('somethingOutOfStock')
                    });

                    const outOfStockIds = error.keycodes.map(code => code.split(':')[1]);

                    setServerCart(
                        Cart.toServer(Cart.removeSelectionWithProductIds(Cart.parse(serverCart), outOfStockIds))
                    )
                    await refreshMenu();

                    openDrawer.right();

                } else if (e.mainError == 'menu_sales_not_available') {
                    pushSnack({
                        type: 'warning',
                        icon: 'warning',
                        text: translate('salesAreNotAvailable')
                    });
                    window.location = service._build_url(`tripay/${slug}/qr/`);

                } else {
                    // Validation error, the menu changed.
                    pushAlert({
                        title: translate('menuChanged'),
                        text: translate('menuChangedMessage'),
                        actions: [{
                            displayText: translate('Accept'),
                            function: () => {
                                return true;
                            }
                        }]
                    }, {
                        onDismiss: async () => {
                            setServerCart(INITIAL_SERVER_CART);
                            await refreshMenu();
                        }
                    })
                }
            }
        }
    }

    const debouncedUpdateUpselling = useCallback(
        debounce(updateUpselling, 500),
        []
    );

    useEffect(() => {
        debouncedUpdateUpselling(serverCart);
    }, [serverCart]);

    const areas = useMemo(() => ProductNode.formatAreas(ProductNode.standaloneProducts(menu)), [menu]);

    const usedPromotions = useMemo(() => Cart.findPromotionUses(Cart.parse(serverCart)), [serverCart]);
    const tempPromotions = useMemo(() => objectFilter(Promotion.updatePromotionUses(JSON.parse(JSON.stringify(promotions)), usedPromotions), p => Promotion.maxUses(p) > 0), [promotions, usedPromotions]);
    const { coupons, offers } = useMemo(() => Promotion.collectionSplitByType(tempPromotions), [tempPromotions]);

    const stackedCart = useMemo(() => {
        return Cart.stackSelections(Cart.addSelectionIds(Cart.parse(serverCart)));
    }, [serverCart]);

    const cartCount = useMemo(() => {
        let total = 0;
        let grouped = {};

        const addToGrouped = (id, qty = 1) => {
            if (id in grouped) {
                grouped[id] += qty;
            } else {
                grouped[id] = qty;
            }
        }

        Object.keys(stackedCart.selections).forEach(selId => {
            const sel = stackedCart.selections[selId];
            addToGrouped(CartSelection.getAreaMainSelection(sel, areas), sel.quantity);
            total += sel.quantity;
        })

        Object.keys(stackedCart.packs).forEach(pIndex => {
            const pack = stackedCart.packs[pIndex];
            addToGrouped(pack.packId, pack.quantity);
            total += pack.quantity * pack.length;
        });

        return { total, grouped };
    }, [stackedCart]);

    const productsInfo = useMemo(() => {
        const ret = {};

        // Add standalone products info
        for (const _selId in stackedCart.selections) {
            ret[_selId] = CartSelection.getProductInfo(stackedCart.selections[_selId], menu);
        }

        // Add pack products info
        for (const _packIndex in stackedCart.packs) {
            const selections = stackedCart.packs[_packIndex].selections;
            for (const _selId in selections) {
                ret[_selId] = CartSelection.getProductInfo(selections[_selId], menu);
            }
        }

        return ret;
    }, [stackedCart]);

    const totalPrice = useMemo(() => {
        let price = Cart.calculateStandalonePrices(Cart.parse(serverCart), menu, promotions);

        price += Object.keys(stackedCart.packs).reduce((prevPacksPrice, packIndex) => {
            const cartPack = stackedCart.packs[packIndex];
            const selectionPrices = CartSelection.collectionCalculatePrices(cartPack.selections, menu);

            const selectionByPackItems = Object.keys(cartPack.selections).reduce((acc, selId) => {
                const packItemId = cartPack.selections[selId].item.packId;
                if (packItemId in acc) {
                    acc[packItemId].push(cartPack.selections[selId]);
                } else {
                    acc[packItemId] = [cartPack.selections[selId]];
                }
                return acc;
            }, {});

            const packPrice = Object.keys(selectionByPackItems).reduce((acc, packItemId) => {
                const packItem = packs[cartPack.packId].items[packItemId];
                const selections = selectionByPackItems[packItemId];
                let itemPrice = selections.reduce((prev, sel) => prev + selectionPrices[sel._selectionId] * sel.quantity, 0)
                if (packItem.coupon) {
                    itemPrice = Coupon.discountedPrice(packItem.coupon, itemPrice);
                }
                return acc + itemPrice;
            }, 0);

            return prevPacksPrice + packPrice * cartPack.quantity;
        }, 0);

        // Apply order offers
        serverCart.promotion_codes.forEach(promotionId => {
            const promotion = promotions[promotionId];
            price = Promotion.discountedPrice(promotion, price);
        })
        // const orderOffers = offers.order;
        // if (Object.keys(orderOffers).length) {
        //     const bestOrderOffer = Promotion.calculateBestOffer(orderOffers, price);
        //     if (bestOrderOffer.promotionId) {
        //         price = bestOrderOffer.price;
        //     }
        // }

        // const packCount = {}, packLengths = {}, packSelections = {};
        // serverCart.selections.forEach(sel => {
        //     console.log(sel);

        //     if (sel._pack_index in packSelections) {

        //     } else {
        //         packSelections[sel._packIndex] = {

        //         }
        //     }
        // })

        return price;
    }, [serverCart.selections.length, menu, packs, stackedCart])

    useEffect(() => {
        const orderOffers = offers.order;
        if (Object.keys(orderOffers).length) {
            const bestOrderOffer = Promotion.calculateBestOffer(orderOffers, totalPrice);
            if (bestOrderOffer.promotionId) {
                if (serverCart.promotion_codes.indexOf(bestOrderOffer.promotionId) < 0) {
                    setServerCart({
                        ...serverCart,
                        promotion_codes: [bestOrderOffer.promotionId]
                    })
                }
            }
        }
    }, [serverCart, offers]);

    useEffect(() => {
        if (cartCount.total > 0 && buttonAnimation.__getValue() === 0) {
            Animated.timing(buttonAnimation, Animation.short(1)).start();
        } else if (cartCount.total <= 0 && buttonAnimation.__getValue() === 1) {
            Animated.timing(buttonAnimation, Animation.short(0)).start();
        }
    }, [cartCount.total])

    useEffect(() => {
        if (!popupSponsorPhoto) return;
        if (!shouldShowPopup(popupSponsorPhoto, eventId)) return;

        setTimeout(() => {
            pushModal({
                children: <View pointerEvents="none" style={{ width: dWidth - 2 * Spacing.MARGINS, height: dHeight - 2 * Spacing.MARGINS }}>
                    <AspectRatioImage source={{ uri: popupSponsorPhoto }} style={{ width: '100%', height: '100%' }} />
                    {!!popupSponsorUrl &&
                        <View>
                            <Button onPress={() => {
                                Linking.openURL(popupSponsorUrl)
                            }} title={popupSponsorCta} />

                        </View>
                    }
                </View>,
                style: {
                    justifyContent: 'center',
                    alignItems: 'center',
                }
            });
            setLastPopup(popupSponsorPhoto, eventId);
        }, 1500);
    }, []);

    // CART FUNCTIONS

    // serverItem can be a list of items or an item
    const addToCartPress = (serverItem, { quantity, selectedPromotions = {} }) => {
        const selections = serverCart.selections;

        const nItems = serverItem.length || 1;
        // serverItem.item.promotion_codes = promotionIds;

        const addSelections = Array.from({ length: quantity }, _ => JSON.parse(JSON.stringify(serverItem))).flat();

        // Add promotions
        Object.keys(selectedPromotions).forEach(promoId => {
            const { quantity } = selectedPromotions[promoId];
            addSelections.slice(0, quantity).forEach(sItem => {
                sItem.item.promotion_codes.push(promoId);
            })
        })

        let isPackItem = false;
        addSelections.forEach(sel => {
            if (sel.item.pack_item) {
                sel._pack_index = totalPacks.current;
                sel._pack_length = nItems;
                isPackItem = true;
            }
        });

        if (isPackItem) totalPacks.current++;

        setServerCart({
            ...serverCart,
            selections: [...selections, ...addSelections],
        })
    };

    const displayItemModal = useMemo(() => AddItemModal.display({
        addToCartPress,
        coupons,
        offers,
        showStepper: true,
    }), [addToCartPress, coupons, offers]);

    const productCardPress = (fungibleParent) => (option) => {
        const selectionTree = SelectionTree.init(fungibleParent);
        const nodeId = option.nodeId;
        SelectionTree.mark(selectionTree, { nodeId, value: true });

        const availableOffers = offers.products[fungibleParent.nodeId];
        const availableCoupons = coupons.products[fungibleParent.nodeId];
        const nCoupons = availableCoupons ? Object.keys(nCoupons).length : 0;

        const price = SelectionTree.calculatePrice(selectionTree);
        const {
            promotionId: appliedOffer,
        } = Promotion.calculateBestOffer(availableOffers || {}, price);

        const selectedPromotions = {};
        if (appliedOffer) {
            selectedPromotions[appliedOffer] = {
                isOffer: true,
                quantity: 1
            }
        }

        const _trackConfig = {
            fungible: fungibleParent.nodeId,
            option: option.nodeId,
        }

        if (
            // Selection tree is full
            selectionTree.marked && selectionTree.solved && selectionTree.valid &&
            // There is no coupon selection
            nCoupons < 1
        ) {
            addToCartPress(SelectionTree.toServer(SelectionTree.addMainSelection(selectionTree, fungibleParent)), { quantity: 1, selectedPromotions });

            screenTracker.tap('ProductCard', {
                ..._trackConfig,
                action: 'addToCart'
            })

        } else {
            displayItemModal(fungibleParent)(option, selectionTree);
            screenTracker.tap('ProductCard', {
                ..._trackConfig,
                action: 'display.AddItemModal'
            })
        }

    }

    const packCardPress = (pack) => {
        screenTracker.tap('PackCard', {
            action: 'display.AddPackModal',
            pack: pack.packId
        });

        const packAreas = ProductPack.getModalAreas(pack);
        const initialSelections = ProductPack.getInitialSelections(pack, packAreas);
        const isComplete = initialSelections.every(s => SelectionTree.isComplete(s));
        const itemIdByProductId = objectMap(ProductPack.getProductIds(pack, { repetitions: false, sorted: false }), 'productId')

        if (isComplete) {
            // Add main selection since we are not showing the modal
            initialSelections.forEach(s => s._mainSelection = s.nodeId);
            addToCartPress(initialSelections.map(s => SelectionTree.toServer(s, { itemIdMap: itemIdByProductId, packId: pack.packId })), { quantity: 1 });
            return;
        }

        pushModal({
            children: <AddPackModal
                style={{ maxHeight: Commons.dHeight - Spacing.NAVBAR * 2, width: Commons.dWidth - Spacing.MARGINS * 2 }}
                pack={pack}
                itemIdByProductId={itemIdByProductId}
                initialSelections={initialSelections}
                addToCartPress={addToCartPress}
                areas={packAreas}
            />,
            style: {
                justifyContent: 'center',
                alignItems: 'center',
            }
        })

    }

    const deleteSelection = (_selId) => {
        screenTracker.tap('DeleteSelectionButton', {
            action: 'deleteFromCart',
        })

        const cart = Cart.unstackSelection(stackedCart);

        setServerCart(Cart.toServer({
            ...cart,
            selections: cart.selections.filter(s => s._selectionId !== _selId)
        }));
    }

    /**
     *
     * @param {CartSelection[]} selections
     */
    const addSelectionsToCart = (selections) => {
        setServerCart({
            ...serverCart,
            selections: [...serverCart.selections, ...selections.map(s => CartSelection.toServer(s))]
        })
    }

    const setSelectionQuantity = (_selId) => (quantity) => {
        screenTracker.tap('ProductStepper', {
            action: 'updateProductQuantity',
            quantity: quantity + ''
        })

        const cart = Cart.unstackSelection({
            ...stackedCart,
            selections: {
                ...stackedCart.selections,
                [_selId]: {
                    ...stackedCart.selections[_selId],
                    quantity
                }
            },
        });

        setServerCart(Cart.toServer({
            ...cart,
            selections: cart.selections
        }));
    }


    const setPackQuantity = (_packIndex, cartPack) => (quantity) => {
        screenTracker.tap('PackStepper', {
            action: 'updatePackQuantity',
            quantity: quantity + ''
        })

        const cart = Cart.parse(serverCart);

        const selections = [];

        // Add those which are not in the pack
        cart.selections.forEach(s => {
            if (s._packIndex != _packIndex) {
                selections.push(s)
            }
        })

        // Add the pack ones, unrolling them first
        const singlePack = Object.keys(cartPack.selections).flatMap(_selId => {
            const itemSel = cartPack.selections[_selId];
            return new Array(itemSel.quantity).fill(itemSel)
        })

        const packSelections = (new Array(quantity).fill(singlePack)).flat();
        selections.push(...packSelections);

        setServerCart(Cart.toServer({ ...cart, selections }));

    }


    const setCartFromOrder = (order) => {
        setServerCart({
            ...serverCart,
            selections: Order.getServerSelections(Order.addMainSelections(order, menu))
        });
        closeDrawer.left();
        openDrawer.right();
    }

    // AREA NAVIGATION

    const addToNavigation = ({ offset, key, title, height }) => {
        let filteredAreas = areas.filter(area => area.fungibleProducts?.length);
        areasLayoutRef.current = {
            ...areasLayoutRef.current,
            [key]: { offset, title, height }
        }

        if (!isRendered.current && Object.keys(areasLayoutRef.current).length === filteredAreas.length) {
            isRendered.current = true;
            setNavbar(areasLayoutRef.current);
        }
    }

    const scrollToArea = (key) => {
        if (menuScollRef.current) {
            menuScollRef.current.programatic = true;
            menuScollRef.current.scrollTo({
                y: navbar[key].offset,
                x: 0
            });
            setTimeout(() => menuScollRef.current.programatic = false, 200);
        }
    }

    // Scrolls to the area containing the fungible with id fungibleId
    const scrollToFungible = (fungibleId) => {
        const area = areas.find(a => a.fungibleProducts.map(n => n.nodeId).indexOf(fungibleId) >= 0);
        if (area) {
            scrollToArea(area.areaId);
            closeDrawer.left();
        }
    }

    const promotionCardPress = (promotion) => {
        const fungibles = Promotion.getValidProducts(promotion);
        if (fungibles && fungibles.length) {
            screenTracker.tap('PromotionCard', {
                promotion: promotion.promotionId,
                action: 'scrollToFungible',
                fungible: fungibles[0]
            })
            scrollToFungible(fungibles[0]);
        }
    }

    // DRAWER
    const openDrawer = {
        right: () => {
            window.history.pushState({ drawer: 'right' }, '')
            Animated.timing(rightDrawerAnim, Animation.short(2)).start();
        },
        left: () => {
            window.history.pushState({ drawer: 'left' }, '')
            Animated.timing(leftDrawerAnim, Animation.short(2)).start();
        }
    }

    const closeDrawer = {
        right: () => {
            Animated.timing(rightDrawerAnim, Animation.short(0)).start();
        },
        left: () => {
            Animated.timing(leftDrawerAnim, Animation.short(0)).start();
        }
    }

    useEffect(() => {
        const backAction = () => {
            closeDrawer.right();
            closeDrawer.left();
        }

        const popListener = window.addEventListener('popstate', backAction)
        const backHandler = BackHandler.addEventListener(
            "hardwareBackPress",
            backAction
        );

        return () => {
            window.removeEventListener('popstate', backAction);
            backHandler.remove()
        }
    }, [])

    const _mockSelectBar = () => {
        if (delivery) {
            return {
                serviceType: 'delivery',
                deliveryAddress: delivery,
                pickupBarId: deliveryId,
            }
        }

        if (pickupId) {
            return {
                serviceType: 'prepared',
                pickupBarId: pickupId
            }
        }

        return {};
    }

    const buyAction = async () => {
        try {
            if (!spinnerModalIndex.current) {
                spinnerModalIndex.current = pushModal({
                    children: <View style={{ justifyContent: 'center', flexGrow: 1 }}><ActivityIndicator color={primaryColor || '#fff'} size="large" /></View>,
                    dismissOnPress: false,
                })

                const { deliveryAddress, serviceType, pickupBarId } = _mockSelectBar();
                const { uid: userBeacon } = getBeaconIds();
                const { order } = await Orders.create({
                    ...serverCart,
                    placeId,
                    eventId,
                    serviceType,
                    deliveryAddress: deliveryAddress ? 'Zona VIP' : undefined, // TODO: Remove VipZone delivery
                    pickupBarId,
                    startedAt: loadedAtTimeStamp,
                    userBeacon
                });

                screenTracker.complete('Order', {
                    orderId: order.orderId,
                    acceptedSuggestions: acceptedSuggestions.current.flat(),
                })

                setCartRecovery({
                    cart: serverCart,
                    menuRoot: menu.nodeId,
                    orderId: order.orderId,
                });

                popModal(spinnerModalIndex.current);
                spinnerModalIndex.current = null;

                Trackings.flush();

                window.location = order.paymentUrl;
                return true;
            }
        } catch (error) {
            switch (error.mainError) {
                case 'out_of_stock':
                    pushSnack({
                        type: 'warning',
                        icon: 'warning',
                        text: translate('somethingOutOfStock')
                    })

                    const outOfStockIds = error.keycodes.map(code => code.split(':')[1]);

                    // TODO: Add promotion_codes logic
                    setServerCart(
                        Cart.toServer(Cart.removeSelectionWithProductIds(Cart.parse(serverCart), outOfStockIds))
                    )

                    await refreshMenu();

                    openDrawer.right();

                    break;

                case 'menu_sales_not_available':
                    pushSnack({
                        type: 'warning',
                        icon: 'warning',
                        text: translate('salesAreNotAvailable')
                    })
                    window.location = service._build_url(`tripay/${slug}/qr/`);
                    break;

                case 'pack_uses_exhausted':
                    pushSnack({
                        type: 'warning',
                        icon: 'warning',
                        text: translate('packUsesExhausted')
                    })
                    setServerCart(INITIAL_SERVER_CART);
                    await refreshMenu();
                    break;

                default:
                    pushSnack({
                        type: 'warning',
                        icon: 'warning',
                        text: translate('somethingWentWrong')
                    })
                    setServerCart(INITIAL_SERVER_CART);
                    await refreshMenu();
            }


            popModal(spinnerModalIndex.current);
            return true;
        }
    }

    const showUpselling = () => {
        if (upselling.current?.selections.length) {

            // Check if user has already seen the upselling once
            // @TODO: Uncoment this if we want to show upselling only once.
            // if (upselling.current?.seen >= 1) {
            //     return;
            // }

            upselling.current.seen++;

            const {
                standalone
            } = CartSelection.stackCartSelections(upselling.current.selections);

            const upsellingTitle = upselling.current.title || '';
            const upsellingDescription = upselling.current.description || '';

            const products = {}; // title, photo
            Object.keys(standalone).forEach(selId => {
                const fungible = CartSelection.getFungibleNode(standalone[selId], menu);

                CartSelection.addMainSelection(standalone[selId], fungible);

                products[selId] = {
                    title: fungible.name,
                    photo: fungible.photo,
                }
            });

            upsellingTracker.start('Upselling', {
                selections: Object.keys(standalone).map(selId => {
                    const sel = standalone[selId];
                    return {
                        id: sel.item.nodeId,
                        quantity: sel.quantity,
                    }
                }),
            })

            pushActionSheet({
                children: (value, setValue) => {
                    const trackedSetValue = ({
                        id,
                        quantity
                    }) => {
                        upsellingTracker.tap('Stepper', { action: 'updateQuantity', quantity, id: value[id].item.nodeId });
                        setValue({
                            ...value,
                            [id]: {
                                ...value[id],
                                quantity
                            }
                        });
                    }
                    return <UpsellingModal {...{
                        value,
                        setValue: trackedSetValue,
                        products,
                        modalTitle: upsellingTitle,
                        modalDescription: upsellingDescription,
                    }} />
                },
                initialState: standalone,
                actions: [
                    {
                        displayText: (value) => {
                            const total = Object.keys(value).reduce((acc, id) => acc + value[id].quantity, 0);
                            return `${translate('addNItems', { n: total })}`;
                        },
                        type: 'x-focus',
                        function: async (value) => {
                            try {
                                const total = Object.keys(value).reduce((acc, id) => acc + value[id].quantity, 0);
                                if (total > 0) {
                                    addSelectionsToCart(
                                        CartSelection.unstackCartSelections(value)
                                    )
                                }

                                upsellingTracker.complete('Upselling', {
                                    selections: Object.keys(value).map(selId => {
                                        const sel = value[selId];
                                        return {
                                            id: sel.item.nodeId,
                                            quantity: sel.quantity,
                                        }
                                    }),
                                })

                                acceptedSuggestions.current.push(Object.keys(value).map(selId => {
                                    const sel = value[selId];
                                    return {
                                        id: sel.item.nodeId,
                                        quantity: sel.quantity,
                                    }
                                }));

                                // setFinishUpselling(true);
                                openDrawer.right();
                                return true;
                            } catch (error) {
                                console.log(error);
                                pushSnack({
                                    type: 'warning',
                                    icon: 'warning',
                                    text: translate('somethingWentWrong')
                                })
                            }
                        }
                    }
                ]
            }, {
                onCancel: () => {
                    upsellingTracker.tap('Cancel', { action: 'closeActionSheet ' });
                    openDrawer.right();
                },
            });
            return true;
        }
    }

    const handleOpenCartPress = () => {
        if (!showUpselling()) {
            openDrawer.right();
        }
    }

    const buyPress = async () => {
        if (totalPrice < minimumOrderAmount && totalPrice !== 0) {
            pushSnack({
                text: translate('minimumOrderIs', { totalPrice: parsePrice(minimumOrderAmount, '€') })
            })
            return;
        }

        if (place.maximumOrderAmount !== null && totalPrice > place.maximumOrderAmount) {
            pushSnack({
                text: translate('maximumOrderIsExceeded', { maximumOrderAmount: parsePrice(place.maximumOrderAmount, '€') })
            })
            return;
        }

        if (place.maximumItemsPerOrder !== null && cartCount.total > place.maximumItemsPerOrder) {
            pushSnack({
                text: translate('maximumItemsPerOrderExceeded', { maximumItemsPerOrder: place.maximumItemsPerOrder })
            })
            return;
        }

        await buyAction();
    }

    const promotionWidgetPress = () => {
        screenTracker.tap('PromotionWidget', { action: 'openDrawer.left' });
        openDrawer.left();
        if (promotionDrawerRef.current) {
            promotionDrawerRef.current.focusTextInput();
        }
    }

    const promotionsToShow = Object.keys(promotions).filter(pId => Coupon.appliesToOrder(promotions[pId].coupon) || !promotions[pId].automatic).map(pId => promotions[pId]);

    let showCartWarning;

    if (place.maximumOrderAmount !== null && totalPrice > place.maximumOrderAmount) {
        showCartWarning = {
            text: translate('maximumOrderIsExceeded', { maximumOrderAmount: parsePrice(place.maximumOrderAmount, '€') }),
        }
    } else if (place.maximumItemsPerOrder !== null && cartCount.total > place.maximumItemsPerOrder) {
        showCartWarning = {
            text: translate('maximumItemsPerOrderExceeded', { maximumItemsPerOrder: place.maximumItemsPerOrder }),
        }
    }

    return <SafeAreaView {...props} style={[{ flex: 1, minHeight: '100%', overflow: 'hidden', maxHeight: Commons.dHeight, maxWidth: Commons.dWidth, backgroundColor }]}>
        {/* <TopBar {...{ logoURL, primaryColor }} style={elev2} isDark={isDark} openDrawer={openDrawer} totalItems={cartCount.total} /> */}

        {!!areas && areas.length > 1 &&
            <Navbar ref={navbarRef} navbar={navbar} scrollToArea={scrollToArea} style={{ flexGrow: 0, flexShrink: 0, backgroundColor: primaryColor }} />}

        <PromotionWidget promotionsToShow={promotionsToShow} onPress={promotionWidgetPress} />

        <ScrollView
            ref={menuScollRef}
            scrollEventThrottle={0}
            onScroll={({ nativeEvent }) => {
                const minY = nativeEvent.contentOffset.y;
                const maxY = minY + nativeEvent.layoutMeasurement.height;
                const areasInView = Object.keys(areasLayoutRef.current).filter(k => {
                    const element = areasLayoutRef.current[k];
                    return element.offset + element.height >= minY && element.offset <= maxY;
                }).map(key => ({ ...areasLayoutRef.current[key], key }));
                if (areasInView.length && !menuScollRef.current.programatic) {
                    if (navbarRef) {
                        navbarRef.current.scrollNavBarToKey(areasInView[0].key);
                    }
                }
            }}
            style={[styles.scrollView]}
            contentContainerStyle={{ paddingHorizontal: Spacing.MARGINS, paddingBottom: Spacing.XL + Button.HEIGHT }}
            showsVerticalScrollIndicator={false}
        >
            {Object.keys(packs).map(packId => {
                return <ProductPackCard
                    cartCount={cartCount.grouped[packId]}
                    onPress={() => packCardPress(packs[packId])}
                    pack={packs[packId]}
                    key={packId}
                    style={{ marginBottom: Spacing.M }} />
            })}

            {!!areas &&
                areas.filter(area => area.fungibleProducts?.length).map(area => (
                    <View
                        key={area.areaId}
                        onLayout={({ nativeEvent }) => {
                            addToNavigation({
                                offset: nativeEvent.layout.y,
                                height: nativeEvent.layout.height,
                                key: area.areaId,
                                title: area.name
                            })
                        }}
                        style={{ marginBottom: Spacing.M }}
                    >
                        {areas.length > 1 && <Text style={[Typography.H6, { color: title, marginBottom: Spacing.M }]}>{area.name}</Text>}
                        {area.fungibleProducts.map(fp => (
                            <FungibleCard
                                {...Promotion.collectionSplitByAutomatic(Promotion.collectionFilterByProductId(tempPromotions, fp.nodeId))}
                                cartCount={cartCount.grouped}
                                displayModal={productCardPress(fp.node)}
                                key={fp.nodeId}
                                product={fp.node}
                                style={{ marginBottom: Spacing.M }}
                                options={fp.options} />
                        ))}
                    </View>
                ))
            }

            <View style={[{ alignItems: 'center', justifyContent: 'center', paddingVertical: Spacing.XL, flexGrow: 1 }]}>

                <View style={{ alignItems: 'center', }}>
                    <Text style={[Typography.STRONG]}>{business.name}</Text>
                    <Text style={Typography.BODY}>{business.phone}</Text>
                    <Text>NIF: {business.taxId}</Text>
                    <Text style={{ textAlign: 'center' }}>{address}</Text>

                    <Text>{business.emailAddress}</Text>
                </View>

                <View style={[{ alignItems: 'center', paddingVertical: Spacing.L }]}>
                    <Text style={[Typography.CAPTION, { color: label, transform: [{ translateX: 16 }] }]}>Powered by</Text>
                    <FesteaPay style={{ height: 28, paddingLeft: Spacing.M }} />
                </View>
            </View>
        </ScrollView>



        <Animated.View
            style={[
                elev2, {
                    borderTopWidth: 1,
                    borderColor: line,
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: Spacing.MARGINS,
                    paddingTop: Spacing.S - 1,
                    backgroundColor,
                    transform: [{ translateY: buttonAnimation.interpolate(Animation.keyframes(Spacing.MARGINS + Button.HEIGHT + 24 + Spacing.S, 0)) }]
                }]}
        >
            <Button
                icon="cart"
                type="secondary"
                status={(totalPrice <= 0) ? 'disabled' : 'default'} onPress={() => {
                    screenTracker.tap('MainButton', { action: 'handleOpenCartPress' });
                    handleOpenCartPress();
                }} title={`${translate('checkCart')} · ${parsePrice(totalPrice, '€')}`} />
        </Animated.View>

        <Animated.View style={[StyleSheet.absoluteFill, {
            transform: [{
                translateX: rightDrawerAnim.interpolate(Animation.keyframes(Commons.dWidth, 0, 0))
            }]
        }]}>
            <AnimatedPressable onPress={closeDrawer.right} style={[StyleSheet.absoluteFill, {
                backgroundColor: Colors.setOpacity('#000000', .5),
                opacity: rightDrawerAnim.interpolate(Animation.keyframes(0, 0, 1))
            }]} />
            <View style={[elev12, { flex: 1, width: Commons.dWidth * 3 / 4, alignSelf: 'flex-end' }]}>
                <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{ paddingBottom: Spacing.M + Spacing.MARGINS + Button.HEIGHT }}>
                    {!!stackedCart?.packs &&
                        Object.keys(stackedCart.packs).sort().map((_packIndex, i) => {
                            const cartPack = stackedCart.packs[_packIndex];
                            const pack = packs[cartPack.packId];
                            const packPrices = CartSelection.collectionCalculatePrices(cartPack.selections, menu);
                            return <CartPackRow
                                {...{
                                    style: {
                                        paddingHorizontal: Spacing.L,
                                        paddingVertical: Spacing.M,
                                        borderBottomWidth: 1,
                                        borderColor: line,
                                        backgroundColor: i % 2 ? inputBackground : backgroundColor
                                    },
                                    cartPack,
                                    pack,
                                    productsInfo,
                                    packPrices,
                                    setPackQuantity: setPackQuantity(_packIndex, cartPack)
                                }}
                            />
                        })
                    }
                    {!!stackedCart?.selections &&
                        Object.keys(stackedCart.selections).sort().map((_selId, i) => {
                            const itemPrice = CartSelection.calculatePrice(stackedCart.selections[_selId], menu, promotions);
                            const promotionCodes = stackedCart.selections[_selId].item.promotionCodes
                            const itemPromotions = promotionCodes.map(pId => promotions[pId]);
                            const itemTempPromotions = promotionCodes.map(pId => tempPromotions[pId]);
                            const qty = stackedCart.selections[_selId].quantity;
                            const maxRemainingUses = itemTempPromotions.length ? Math.min(itemTempPromotions.map(p => Promotion.maxUses(p))) : undefined;
                            return <CartRow
                                style={{ paddingHorizontal: Spacing.L, borderBottomWidth: 1, borderColor: line, backgroundColor: (i + Object.keys(stackedCart.packs).length) % 2 ? inputBackground : backgroundColor }}
                                cartPrice={itemPrice * qty}
                                key={_selId}
                                deleteSelection={() => deleteSelection(_selId)}
                                setSelectionQuantity={(v) => setSelectionQuantity(_selId)(v)}
                                productsInfo={productsInfo[_selId]}
                                cartSelection={stackedCart.selections[_selId]}
                                promotions={itemPromotions}
                                maxQuantity={(maxRemainingUses + qty) || undefined}
                            />
                        })
                    }
                </ScrollView>
                {serverCart.selections.length <= 0 ?
                    <>
                        <Text style={[Typography.SMALL_COPY, { color: body, textAlign: 'center', marginHorizontal: Spacing.MARGINS }]}>{translate('cartIsEmpty')}</Text>
                        <View style={{ flex: 1 }} />
                    </> :
                    <>
                        {showCartWarning &&
                            <WarningBanner
                                text={showCartWarning.text}
                                style={{ bottom: Spacing.S + Spacing.MARGINS + Button.HEIGHT, left: Spacing.MARGINS, right: Spacing.MARGINS, position: 'absolute' }} />
                        }

                        <Button
                            style={{ bottom: Spacing.MARGINS, left: Spacing.MARGINS, right: Spacing.MARGINS, position: 'absolute' }}
                            status={(totalPrice < minimumOrderAmount && totalPrice !== 0) ? 'disabled' : 'default'}
                            loadOnPress onPress={() => {
                                screenTracker.tap('CartBuyButton', { action: 'buyPress' });
                                buyPress();
                            }} title={`${translate('buy')} · ${parsePrice(totalPrice, '€')}`} />
                    </>
                }
            </View>
        </Animated.View>


        <Animated.View style={[StyleSheet.absoluteFill, {
            transform: [{
                translateX: leftDrawerAnim.interpolate(Animation.keyframes(-Commons.dWidth, 0, 0))
            }]
        }]}>
            <AnimatedPressable onPress={closeDrawer.left} style={[StyleSheet.absoluteFill, {
                backgroundColor: Colors.setOpacity('#000000', .5),
                opacity: leftDrawerAnim.interpolate(Animation.keyframes(0, 0, 1))
            }]} />
            <View style={[elev12, { flex: 1, width: Commons.dWidth * 3 / 4, alignSelf: 'flex-start' }]}>
                <PromotionsDrawer ref={promotionDrawerRef} style={{ flex: 1 }} tracker={screenTracker} {...{ promotions, addPromotions, place, promotionPress: promotionCardPress }} />
            </View>
        </Animated.View>
    </SafeAreaView>
}
